import { type CSSProperties } from 'react';
import { HistoryChartSkeleton } from './WatchlistInsights/HistoryChartSkeleton';
import styles from './WatchlistInsightsSkeleton.module.css';
import { NewsListSkeleton } from './newsfeed/utils';
import {
  ANALYST_RATINGS_SKELETON_COLUMNS,
  getResearchTableSkeletonRowCount,
  RESEARCH_PANEL_BODY_HEIGHT,
  PORTFOLIO_CARD_CLASSNAME,
  WATCHLIST_QUOTES_SYMBOL_CELL_CLASS,
  WATCHLIST_QUOTES_TABLE_FRAME_CLASS,
} from './portfolioLayoutConstants';
import {
  PORTFOLIO_TABLE_CELL_CLASS,
  WATCHLIST_PANEL_TOOLBAR_CLASS,
  WatchlistQuotesStaticTableHead,
} from './FinancialTable/watchlistQuotesTableChrome';
import { widgetTitleClassName } from '../../../edge/dashboard/_components/widgetChrome';

const Bone = ({ className = '', style }: { className?: string; style?: CSSProperties }) => (
  <div className={`${styles.bone} rounded ${className}`} style={style} />
);

const BoneLight = ({ className = '', style }: { className?: string; style?: CSSProperties }) => (
  <div className={`${styles.boneLight} rounded ${className}`} style={style} />
);

export const TableCellSkeleton = ({ className = '' }: { className?: string }) => (
  <BoneLight className={`block min-h-4 ${className}`} />
);

const legendWidths = [88, 72, 64, 56, 48];

const PerformerSkeleton = ({ label }: { label: string }) => (
  <div className="flex h-full min-h-[7.5rem] flex-col gap-2 bg-bzblue-800 p-3">
    <p className="text-xs font-medium text-slate-300">{label}</p>
    <div className="flex flex-1 items-center">
      <div className="flex items-baseline gap-2">
        <Bone className="h-7 w-16" />
        <Bone className="h-4 w-12" />
      </div>
    </div>
    <Bone className="h-7 w-14 shrink-0 rounded-md" />
  </div>
);

const MixChartSkeleton = ({ label }: { label: string }) => (
  <div className="flex h-full min-h-[7.5rem] flex-col gap-2 bg-bzblue-800 p-3">
    <p className="text-xs font-medium text-slate-300">{label}</p>
    <div className="flex flex-1 items-center gap-2.5">
      <div className="relative size-[92px] shrink-0">
        <div className={`${styles.donut} absolute inset-0 rounded-full`} />
        <div className="absolute inset-[30%] rounded-full bg-bzblue-800" />
      </div>
      <ul className="grid min-w-0 flex-1 grid-cols-2 gap-x-2 gap-y-1">
        {legendWidths.map((width, index) => (
          <li key={index} className="flex items-center gap-1.5">
            <Bone className="size-2 shrink-0 rounded-sm" />
            <Bone className="h-3 min-w-0 flex-1" style={{ maxWidth: width }} />
          </li>
        ))}
      </ul>
    </div>
  </div>
);

export const WatchlistInsightsSkeleton = () => (
  <div className="overflow-hidden rounded-lg" aria-busy="true" aria-live="polite">
    <div className="bg-bzblue-700 p-6 text-white">
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        <div className="max-w-xl space-y-3">
          <div className="text-xl font-bold">Your Daily Overview</div>
          <Bone className="h-9 w-[min(100%,22rem)]" />
          <Bone className="h-4 w-[min(100%,28rem)]" />
          <Bone className="h-4 w-[min(100%,18rem)]" />
          <div className="mt-4 flex flex-wrap gap-3">
            <Bone className="h-8 w-36 rounded-lg" />
            <Bone className="h-8 w-24 rounded-lg" />
            <Bone className="h-8 w-28 rounded-lg" />
          </div>
        </div>
        <div className="flex flex-col justify-end gap-3 md:items-end">
          <Bone className="h-7 w-44" />
          <Bone className="h-2.5 w-full max-w-sm rounded-full md:ml-auto" />
          <div className="flex w-full max-w-sm justify-between md:ml-auto">
            <Bone className="h-3 w-20" />
            <Bone className="h-3 w-20" />
          </div>
        </div>
      </div>
    </div>

    <HistoryChartSkeleton />

    <section className="border-t border-white/10 bg-bzblue-800 text-white">
      <div className="flex items-center justify-between border-b border-white/10 px-4 py-2.5">
        <Bone className="h-4 w-32" />
        <Bone className="h-3 w-20" />
      </div>
      <div className="grid grid-cols-1 gap-px bg-white/10 sm:grid-cols-2 lg:grid-cols-4">
        <PerformerSkeleton label="Best performer" />
        <PerformerSkeleton label="Worst performer" />
        <MixChartSkeleton label="Sectors" />
        <MixChartSkeleton label="Regions" />
      </div>
    </section>
  </div>
);

export const EdgeIdeaCardSkeleton = () => (
  <div className="flex min-h-[124px] flex-col justify-between gap-3 rounded-xl border border-bzgray-800 bg-[#192940] p-4">
    <div className="space-y-2">
      <Bone className="h-4 w-[92%]" />
      <Bone className="h-4 w-[70%]" />
    </div>
    <div className="flex flex-col gap-2">
      <div className="flex gap-1.5">
        <Bone className="h-5 w-12 rounded-md" />
        <Bone className="h-5 w-12 rounded-md" />
        <Bone className="h-5 w-10 rounded-md" />
      </div>
      <Bone className="h-3 w-32" />
    </div>
  </div>
);

export const EdgePortfolioCardSkeleton = () => (
  <div className="flex min-h-[148px] flex-col gap-4 rounded-xl border border-bzgray-800 bg-[#192940] p-4 sm:p-5">
    <div className="flex items-start justify-between gap-3">
      <div className="flex min-w-0 items-start gap-3">
        <Bone className="size-10 shrink-0 rounded-xl" />
        <div className="min-w-0 space-y-2 pt-0.5">
          <Bone className="h-5 w-40 sm:w-48" />
          <Bone className="h-4 w-full max-w-[18rem]" />
          <Bone className="h-4 w-[88%] max-w-[15rem]" />
        </div>
      </div>
      <Bone className="h-6 w-20 shrink-0 rounded-full" />
    </div>
    <div className="mt-auto flex items-center justify-between gap-3 border-t border-white/10 pt-3">
      <Bone className="h-3 w-28" />
      <Bone className="h-3 w-24" />
    </div>
  </div>
);

export const PeerCardSkeleton = () => (
  <div className="flex items-start justify-between gap-3 rounded-lg border border-slate-200 bg-white p-3.5">
    <div className="flex min-w-0 flex-1 items-start gap-2">
      <BoneLight className="size-5 shrink-0 rounded-[5px]" />
      <div className="min-w-0 flex-1 space-y-2">
        <BoneLight className="h-4 w-[70%]" />
        <BoneLight className="h-3 w-12" />
        <BoneLight className="h-4 w-16 rounded" />
      </div>
    </div>
    <div className="shrink-0 space-y-1.5 text-right">
      <BoneLight className="ml-auto h-4 w-14" />
      <BoneLight className="ml-auto h-2.5 w-16" />
    </div>
  </div>
);

export const PeersGridSkeleton = ({ count = 8 }: { count?: number }) => (
  <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4" aria-busy="true" aria-live="polite">
    {Array.from({ length: count }).map((_, index) => (
      <PeerCardSkeleton key={index} />
    ))}
  </div>
);

const FeedTabPillsSkeleton = ({ labels }: { labels: string[] }) => (
  <div className="mb-4 flex flex-wrap gap-2">
    {labels.map((label, index) => (
      <span
        key={label}
        className={`rounded-md px-3.5 py-2 text-xs font-bold ${
          index === 0 ? 'bg-bzblue-700 text-transparent' : 'bg-slate-100 text-transparent'
        }`}
      >
        {label}
      </span>
    ))}
  </div>
);

export const CalendarFeedSkeleton = () => (
  <div aria-busy="true" aria-live="polite">
    <FeedTabPillsSkeleton labels={['Earnings', 'Calls', 'Dividends', 'Splits']} />
    <div>
      {Array.from({ length: 2 }).map((_, index) => (
        <article key={index} className="border-b border-slate-100 py-4 first:pt-0 last:border-b-0 last:pb-0">
          <BoneLight className="h-5 w-[min(100%,18rem)]" />
          <div className="mt-3 space-y-2 rounded-lg bg-slate-50 px-3 py-3">
            <div className="flex items-center justify-between gap-3">
              <BoneLight className="h-3 w-16" />
              <BoneLight className="h-3.5 w-20" />
            </div>
            <div className="flex items-center justify-between gap-3">
              <BoneLight className="h-3 w-14" />
              <BoneLight className="h-3.5 w-16" />
            </div>
            <div className="flex items-center justify-between gap-3">
              <BoneLight className="h-3 w-20" />
              <BoneLight className="h-3.5 w-24" />
            </div>
          </div>
          <div className="mt-3 flex items-center gap-2">
            <BoneLight className="h-3 w-10" />
            <BoneLight className="h-3 w-28" />
          </div>
        </article>
      ))}
    </div>
  </div>
);

export const InsidersFeedSkeleton = () => (
  <div aria-busy="true" aria-live="polite">
    <FeedTabPillsSkeleton labels={['All', 'Buys', 'Sells']} />
    <div>
      {Array.from({ length: 4 }).map((_, index) => (
        <article key={index} className="border-b border-slate-100 py-4 first:pt-0 last:border-b-0 last:pb-0">
          <div className="flex flex-wrap items-center gap-2">
            <BoneLight className="h-5 w-40" />
            <BoneLight className="h-5 w-12 rounded-md" />
          </div>
          <BoneLight className="mt-1 h-3 w-28" />
          <div className="mt-2 space-y-1.5">
            <BoneLight className="h-4 w-full" />
            <BoneLight className="h-4 w-[85%]" />
          </div>
          <div className="mt-3 flex items-center gap-2">
            <BoneLight className="h-3 w-12" />
            <BoneLight className="h-3 w-16" />
          </div>
        </article>
      ))}
    </div>
  </div>
);

export const WatchlistQuotesTableSkeleton = ({ rowCount = 8 }: { rowCount?: number }) => (
  <>
    <div className={`sm:hidden overscroll-contain ${WATCHLIST_QUOTES_TABLE_FRAME_CLASS}`} aria-busy="true" aria-live="polite">
      {Array.from({ length: rowCount }).map((_, index) => (
        <div
          key={index}
          className="flex items-center justify-between gap-3 border-b border-slate-100 px-4 py-3 last:border-0"
        >
          <div className={`min-w-0 flex-1 ${WATCHLIST_QUOTES_SYMBOL_CELL_CLASS}`}>
            <BoneLight className="h-4 w-14" />
            <BoneLight className="mt-0.5 h-4 w-28" />
          </div>
          <div className="shrink-0 space-y-0.5 text-right">
            <BoneLight className="ml-auto h-4 w-16" />
            <BoneLight className="ml-auto mt-0.5 h-3 w-12" />
          </div>
        </div>
      ))}
    </div>

    <div className={`hidden sm:block ${WATCHLIST_QUOTES_TABLE_FRAME_CLASS}`} aria-busy="true" aria-live="polite">
      <table className="w-full min-w-[760px] border-collapse text-sm">
        <WatchlistQuotesStaticTableHead />
        <tbody>
          {Array.from({ length: rowCount }).map((_, index) => (
            <tr key={index} className="border-b border-slate-100 last:border-0">
              <td className={PORTFOLIO_TABLE_CELL_CLASS}>
                <div className={WATCHLIST_QUOTES_SYMBOL_CELL_CLASS}>
                  <BoneLight className="h-4 w-14" />
                  <BoneLight className="mt-0.5 h-4 w-24" />
                </div>
              </td>
              <td className={PORTFOLIO_TABLE_CELL_CLASS}>
                <BoneLight className="ml-auto block h-4 w-16" />
              </td>
              <td className={PORTFOLIO_TABLE_CELL_CLASS}>
                <BoneLight className="ml-auto block h-4 w-14" />
              </td>
              <td className={PORTFOLIO_TABLE_CELL_CLASS}>
                <BoneLight className="ml-auto block h-4 w-14" />
              </td>
              <td className={PORTFOLIO_TABLE_CELL_CLASS}>
                <BoneLight className="ml-auto block h-4 w-16" />
              </td>
              <td className={PORTFOLIO_TABLE_CELL_CLASS}>
                <BoneLight className="ml-auto block h-4 w-20" />
              </td>
              <td className={PORTFOLIO_TABLE_CELL_CLASS}>
                <BoneLight className="ml-auto block h-4 w-8" />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </>
);

export const WatchlistPanelSkeleton = () => (
  <div aria-busy="true" aria-live="polite">
    <div className={WATCHLIST_PANEL_TOOLBAR_CLASS}>
      <div className="min-w-0">
        <div className="flex min-h-7 min-w-0 flex-wrap items-center gap-2.5">
          <BoneLight className="h-7 w-44" />
          <BoneLight className="h-5 min-w-[5.75rem] rounded-full" />
        </div>
        <div className="mt-1.5">
          <BoneLight className="h-3.5 w-24" />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2 sm:flex sm:items-center">
        <BoneLight className="h-9 min-h-9 w-full rounded-md sm:w-[5.25rem]" />
        <BoneLight className="h-9 min-h-9 w-full rounded-md sm:w-[4.5rem]" />
        <BoneLight className="col-span-2 h-9 min-h-9 w-full rounded-md sm:col-span-1 sm:w-[7.5rem]" />
      </div>
    </div>
    <WatchlistQuotesTableSkeleton />
    <div className="border-t border-slate-200 p-4">
      <WatchlistInsightsSkeleton />
    </div>
  </div>
);

export const ResearchTableSkeleton = ({
  columns = [],
  height = RESEARCH_PANEL_BODY_HEIGHT,
  rowCount,
}: {
  columns?: string[];
  height?: number;
  rowCount?: number;
}) => {
  const headerLabels = columns.length > 0 ? columns : ['Symbol'];
  const resolvedRowCount = rowCount ?? getResearchTableSkeletonRowCount(height);

  return (
    <div
      className="w-full overflow-hidden"
      style={{ height }}
      aria-busy="true"
      aria-live="polite"
    >
      <div className="h-full overflow-x-auto">
      <table className="w-full min-w-[760px] border-collapse text-sm">
        <thead className="sticky top-0 z-10">
          <tr className="border-b border-slate-200 bg-white">
            {headerLabels.map((label, index) => (
              <th
                key={`${label}-${index}`}
                className={`px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wide text-slate-500 ${
                  index === 0 ? 'text-left' : 'text-right'
                }`}
              >
                {label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: resolvedRowCount }).map((_, rowIndex) => (
            <tr key={rowIndex} className="border-b border-slate-100 last:border-0">
              {headerLabels.map((_, colIndex) => (
                <td key={colIndex} className="h-[52px] px-4 align-middle">
                  {colIndex === 0 ? (
                    <div>
                      <BoneLight className="h-4 w-14" />
                      <BoneLight className="mt-0.5 h-4 w-24" />
                    </div>
                  ) : (
                    <BoneLight className="ml-auto block h-4 w-14" />
                  )}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      </div>
    </div>
  );
};

export const EdgeIdeasDeferredSkeleton = () => (
  <section className={PORTFOLIO_CARD_CLASSNAME} aria-busy="true" aria-live="polite">
    <div className="border-b border-slate-200 px-4 py-3">
      <div className="flex items-center gap-2">
        <BoneLight className="h-4 w-40 sm:w-56" />
        <BoneLight className="h-4 w-14 rounded" />
      </div>
    </div>
    <div className="flex flex-col gap-5 p-5 sm:gap-6 sm:p-6">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4">
        <EdgePortfolioCardSkeleton />
        <EdgePortfolioCardSkeleton />
      </div>
      <div>
        <BoneLight className="mb-3 h-3.5 w-36" />
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4 xl:grid-cols-3">
          {Array.from({ length: 3 }).map((_, index) => (
            <EdgeIdeaCardSkeleton key={index} />
          ))}
        </div>
      </div>
    </div>
  </section>
);

export const PortfolioResearchDeferredSkeleton = () => (
  <section className="grid items-stretch gap-3 lg:grid-cols-[minmax(0,2fr)_minmax(300px,1fr)]" aria-busy="true">
    <div className={`${PORTFOLIO_CARD_CLASSNAME} min-h-0`}>
      <div className="shrink-0 border-b border-slate-200 px-4 py-3">
        <h2 className={widgetTitleClassName}>Research</h2>
        <p className="mt-0.5 text-xs text-slate-500">Ratings, fundamentals, and rankings for this watchlist</p>
        <div className="mt-2 flex w-full gap-1.5 overflow-x-auto">
          {['Analyst Ratings', 'Fundamentals', 'Edge Rankings', 'Metrics'].map((label, index) => (
            <span
              key={label}
              className={`shrink-0 rounded-md px-2.5 py-1 text-xs font-bold text-transparent ${
                index === 0 ? 'bg-[#3F83F8]' : 'bg-[#3F83F81A]'
              }`}
            >
              {label}
            </span>
          ))}
        </div>
      </div>
      <ResearchTableSkeleton columns={ANALYST_RATINGS_SKELETON_COLUMNS} height={RESEARCH_PANEL_BODY_HEIGHT} />
    </div>
    <div className={`${PORTFOLIO_CARD_CLASSNAME} min-h-0`}>
      <div className="shrink-0 border-b border-slate-200 px-4 py-3">
        <h2 className={widgetTitleClassName}>Watchlist News</h2>
        <div className="mt-2 flex w-full gap-1.5 overflow-x-auto">
          {['All', 'Earnings', 'Analyst', 'Insiders', 'Ideas'].map((label, index) => (
            <span
              key={label}
              className={`shrink-0 rounded-md px-2.5 py-1 text-xs font-bold text-transparent ${
                index === 0 ? 'bg-[#3F83F8]' : 'bg-[#3F83F81A]'
              }`}
            >
              {label}
            </span>
          ))}
        </div>
      </div>
      <div
        className="flex-1 overflow-y-auto px-2 py-1.5 sm:px-3"
        style={{ height: RESEARCH_PANEL_BODY_HEIGHT }}
      >
        <NewsListSkeleton compact count={8} showTimeAgo />
      </div>
    </div>
  </section>
);

export const EdgeBannerDeferredSkeleton = () => (
  <div className={PORTFOLIO_CARD_CLASSNAME} aria-busy="true" aria-live="polite">
    <div className="flex flex-col gap-4 p-4 sm:flex-row sm:items-center sm:justify-between">
      <div className="min-w-0 flex-1 space-y-2">
        <BoneLight className="h-5 w-40" />
        <BoneLight className="h-4 w-[min(100%,24rem)]" />
        <BoneLight className="h-4 w-[min(100%,18rem)]" />
      </div>
      <BoneLight className="h-10 w-36 shrink-0 rounded-md" />
    </div>
  </div>
);

export const PeersDeferredSkeleton = () => (
  <section className={PORTFOLIO_CARD_CLASSNAME} aria-busy="true" aria-live="polite">
    <div className="border-b border-slate-200 px-4 py-3">
      <h2 className={widgetTitleClassName}>Suggested Peers</h2>
      <p className="mt-0.5 text-xs text-slate-500">Related tickers in similar sectors</p>
    </div>
    <div className="p-4">
      <PeersGridSkeleton />
    </div>
  </section>
);
